// Word-level diff utility used to render the "Corrected Report" tab.
// Compares the AI-generated final_report (baseline) against the user's
// corrected_report and highlights genuinely changed/added words in yellow.
//
// The diff runs on the WHOLE document as a single token stream (not
// line-by-line) so a shift in blank lines or spacing doesn't cascade into
// unrelated lines looking "changed". It also ignores literal **bold**
// markers when comparing, since those are stripped whenever the report is
// edited through the rich-text/contentEditable box -- that's a formatting
// artifact, not a real correction, and would otherwise make every bolded
// field label look edited.

function tokenize(text) {
  return (text || '').split(/(\s+)/).filter((t) => t !== '');
}

// Token used only for comparison purposes -- strips ** markers so bold
// formatting differences don't register as content changes.
const compareKey = (t) => t.replace(/\*\*/g, '');

function lcsBacktrack(a, b) {
  const ak = a.map(compareKey);
  const bk = b.map(compareKey);
  const n = a.length, m = b.length;
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = ak[i] === bk[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (ak[i] === bk[j]) { ops.push({ type: 'equal', value: b[j] }); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push({ type: 'remove', value: a[i] }); i++; }
    else { ops.push({ type: 'add', value: b[j] }); j++; }
  }
  while (i < n) { ops.push({ type: 'remove', value: a[i] }); i++; }
  while (j < m) { ops.push({ type: 'add', value: b[j] }); j++; }
  return ops;
}

const escHtml = (s) =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/  /g, ' &nbsp;');
const boldify = (s) => s.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

function plainLinesHtml(text) {
  return (text || '')
    .split('\n')
    .map((l) => (l.trim() ? `<div class="rr-line">${boldify(escHtml(l.trim()))}</div>` : '<div style="height:14px"></div>'))
    .join('');
}

/**
 * Builds HTML for the Corrected Report tab: the corrected text, with any
 * word(s) that differ from the baseline final report wrapped in a yellow
 * <mark>. Falls back to plain rendering if there is no baseline to diff against.
 */
export function renderCorrectedReportHtml(finalText, correctedText) {
  const corrected = correctedText || '';
  if (!corrected) return '';
  if (!finalText) return plainLinesHtml(corrected);

  const ops = lcsBacktrack(tokenize(finalText), tokenize(corrected));

  // Keep only tokens on the corrected side (equal + add) -- concatenating
  // them in order exactly reconstructs the corrected text.
  const kept = ops.filter((o) => o.type !== 'remove').map((o) => ({ value: o.value, changed: o.type === 'add' }));

  // Split into lines on whitespace tokens containing newline(s).
  const lines = [[]];
  for (const tok of kept) {
    if (tok.value.includes('\n')) {
      const newlineCount = (tok.value.match(/\n/g) || []).length;
      for (let n = 0; n < newlineCount; n++) lines.push([]);
    } else {
      lines[lines.length - 1].push(tok);
    }
  }

  let html = '';
  for (const line of lines) {
    const text = line.map((t) => t.value).join('');
    if (!text.trim()) { html += '<div style="height:14px"></div>'; continue; }

    let lineHtml = '';
    let i = 0;
    while (i < line.length) {
      const changed = line[i].changed;
      let chunk = '';
      while (i < line.length && line[i].changed === changed) { chunk += line[i].value; i++; }
      const rendered = boldify(escHtml(chunk));
      lineHtml += changed ? `<mark class="diff-highlight">${rendered}</mark>` : rendered;
    }
    html += `<div class="rr-line">${lineHtml}</div>`;
  }
  return html;
}

/** True if the corrected text differs from the baseline final report (ignoring bold markers). */
export function hasCorrections(finalText, correctedText) {
  if (!correctedText) return false;
  const normalize = (t) => (t || '').replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  return normalize(finalText) !== normalize(correctedText);
}